/**
 * Journey: IVF, With Donor (sperm), Female
 *
 * Steps: Learn > Test > Clinic > Prep > Treatment > Wait
 *
 * For a woman using donor sperm who may have a non-biological partner
 * for support. Similar to solo but acknowledges the support partner.
 */

import type { JourneyDefinition, PlanStep } from "../plan.types";

const learnStep: PlanStep = {
  id: "learn",
  title: "How IVF Works",
  subtitle: "The most effective fertility treatment",
  icon: "BookOpen",
  education: {
    context:
      "IVF (in vitro fertilization) creates embryos outside the body, selects the best one, and transfers it to your uterus. It's the most effective fertility treatment, with success rates of 40-50% per transfer for women under 35.",
    details:
      "The full process:\n1. Ovarian stimulation (10-14 days): Injectable medications help multiple eggs mature\n2. Egg retrieval (20 minutes): Eggs collected under light sedation\n3. Fertilization (day 0): Your eggs are fertilized with donor sperm in the lab\n4. Embryo culture (5-7 days): Embryos develop to blastocyst stage\n5. Embryo transfer (5 minutes): A single embryo is placed in your uterus\n6. Two-week wait: You wait ~14 days for a blood pregnancy test (beta hCG)\n\nYou'll select a sperm donor through a sperm bank or your clinic's donor program. Your partner can support you throughout the process.",
    learnMoreLink: "ivf-guide",
  },
  actions: [
    {
      label: "I understand this",
      variant: "primary",
      icon: "Check",
      signal: "complete-step",
    },
    {
      label: "Full IVF guide",
      variant: "outline",
      icon: "BookOpen",
      screen: "LearnScreen",
      params: { article: "ivf-guide" },
    },
  ],
  completionSignal: { type: "manual" },
  conditionModifiers: [
    {
      condition: "pcos",
      additionalEducation:
        "You mentioned PMOS (ex PCOS). This affects your IVF protocol: you may respond very strongly to stimulation (risk of OHSS), your clinic will likely use lower medication doses and monitor more frequently, and Letrozole trigger or antagonist protocols are common. You may produce more eggs, but quality can vary. PGT-A testing helps select the best embryo.",
    },
    {
      condition: "endometriosis",
      additionalEducation:
        "You mentioned endometriosis. Endometriosis can affect egg quality, especially with ovarian endometriomas. Your clinic may recommend a longer suppression phase before stimulation. Success rates are slightly lower but IVF is still the most effective treatment path.",
    },
    {
      condition: "age-38-plus",
      additionalEducation:
        "At your age, your clinic may recommend PGT-A to screen embryos for chromosomal abnormalities, more aggressive stimulation protocols, banking embryos across multiple cycles before transferring, and setting realistic expectations: success rates per transfer are ~25-30% at 38-40.",
    },
  ],
  ageSpecificContent: [
    {
      range: [0, 34],
      content:
        "Under 35, IVF success rates are 40-50% per transfer. Most women conceive within 1-2 cycles.",
    },
    {
      range: [35, 37],
      content:
        "At 35-37, success rates are ~35-40% per transfer. PGT-A testing can help select the best embryo.",
    },
    {
      range: [38, 40],
      content:
        "At 38-40, success rates are ~25-30% per transfer. Multiple cycles may be needed.",
    },
    {
      range: [41, 99],
      content:
        "Over 40, success rates decline significantly. Your RE will discuss options including donor eggs.",
    },
  ],
};

const testStep: PlanStep = {
  id: "test",
  title: "Baseline Fertility Testing",
  subtitle: "Understand your fertility health",
  icon: "TestTube",
  education: {
    context:
      "IVF requires comprehensive testing to plan your protocol. Your clinic needs ovarian reserve data and uterine assessment. Since you're using donor sperm, the donor is already pre-screened: no sperm testing needed on your end.",
  },
  actions: [
    {
      label: "Order at-home AMH test",
      variant: "primary",
      screen: "AtHomeFertilityTesting",
    },
    {
      label: "Upload test results",
      variant: "outline",
      screen: "DocumentsScreen",
    },
  ],
  substeps: [
    {
      id: "amh",
      title: "AMH (Anti-Müllerian Hormone)",
      education:
        "Measures your egg supply. The single most important test for protocol planning.",
      status: "not-started",
    },
    {
      id: "fsh-estradiol",
      title: "FSH + Estradiol",
      education: "Cycle day 2-3 blood test. Shows ovarian function.",
      status: "not-started",
    },
    {
      id: "afc",
      title: "Antral Follicle Count (AFC)",
      education:
        "Transvaginal ultrasound. Combined with AMH, predicts stimulation response.",
      status: "not-started",
    },
    {
      id: "hsg",
      title: "HSG (Hysterosalpingogram)",
      education:
        "X-ray test to check if your fallopian tubes are open. While IVF bypasses the tubes, this reveals uterine abnormalities that affect implantation. Some clinics use a saline sonogram (SIS) instead.",
      status: "not-started",
    },
    {
      id: "infectious-disease",
      title: "Infectious disease screening",
      education:
        "Required before any IVF cycle. Standard panel: HIV, Hepatitis B/C, syphilis, gonorrhea, chlamydia.",
      status: "not-started",
    },
  ],
  partnerTrack: {
    education:
      "Your partner may also want to complete infectious disease screening, which some clinics require for anyone present during procedures.",
    substeps: [
      {
        id: "partner-infectious",
        title: "Partner: Infectious disease screening (optional)",
        education:
          "Some clinics require screening for partners present at procedures.",
        status: "not-started",
        isPartnerTrack: true,
      },
    ],
  },
  completionSignal: { type: "all-substeps" },
};

const clinicStep: PlanStep = {
  id: "clinic",
  title: "Find Your Clinic",
  subtitle: "Success rates matter most for IVF",
  icon: "Building2",
  education: {
    context:
      "For IVF, success rates matter more than for egg freezing. Check SART.org for clinic-specific data. Key metrics: live birth rate per transfer (not just pregnancy rate), and rates for YOUR age bracket. Also ask about their donor sperm coordination. Some clinics have in-house donor programs.",
  },
  actions: [
    {
      label: "Browse clinics near you",
      variant: "primary",
      screen: "FindCareScreen",
      params: { filter: "ivf" },
    },
    {
      label: "Compare clinic costs",
      variant: "outline",
      screen: "CostsOverviewScreen",
    },
  ],
  substeps: [
    {
      id: "research-clinics",
      title: "Research clinics",
      education: "Compare SART success rates for your age bracket.",
      status: "not-started",
    },
    {
      id: "schedule-consultation",
      title: "Schedule a consultation",
      education: "30-60 minute initial consultation to review your case.",
      status: "not-started",
    },
    {
      id: "choose-clinic",
      title: "Choose your clinic",
      education: "Your clinic will start your onboarding process.",
      status: "not-started",
    },
  ],
  completionSignal: { type: "manual" },
};

const prepStep: PlanStep = {
  id: "prep",
  title: "Get Ready for Your Cycle",
  subtitle: "Pre-cycle paperwork and decisions",
  icon: "ClipboardCheck",
  education: {
    context:
      "IVF prep involves medical history, insurance, selecting your sperm donor, and important decisions about genetic testing and transfer strategy.",
  },
  actions: [
    {
      label: "Continue medical history",
      variant: "primary",
      screen: "MedicalHistory",
    },
    {
      label: "Upload insurance card",
      variant: "secondary",
      screen: "CoverageFlow",
    },
  ],
  substeps: [
    {
      id: "medical-history",
      title: "Complete medical history",
      education: "Identifies factors affecting your protocol.",
      status: "not-started",
    },
    {
      id: "insurance-card",
      title: "Upload insurance card",
      education: "Needed for billing.",
      status: "not-started",
    },
    {
      id: "select-donor",
      title: "Select your sperm donor",
      education:
        "Choose a donor through a sperm bank or your clinic's donor program. You can filter by physical characteristics, education, health history, and more. Your clinic can help guide this decision.",
      status: "not-started",
    },
    {
      id: "pgta-decision",
      title: "PGT-A decision",
      education:
        "Decide whether to genetically test embryos before transfer. Cost: $3,000-$5,000. Recommended if: age 35+, history of miscarriage, or prior failed transfers.",
      status: "not-started",
    },
    {
      id: "transfer-plan",
      title: "Transfer plan discussion",
      education:
        "Fresh transfer (same cycle) vs frozen transfer (separate cycle). Your clinic will recommend based on your response to stimulation and embryo quality.",
      status: "not-started",
    },
    {
      id: "consent-forms",
      title: "Sign consent forms",
      education:
        "Covers procedure, risks, embryo disposition, and donor sperm consent.",
      status: "not-started",
    },
    {
      id: "supplements",
      title: "Start supplements (optional)",
      education:
        "CoQ10 and vitamin D for egg quality. Start 2-3 months before if possible.",
      status: "not-started",
      actions: [
        {
          label: "See supplements",
          variant: "outline",
          screen: "SupplementsScreen",
        },
      ],
    },
  ],
  completionSignal: {
    type: "completion-percent",
    field: "prepReadiness",
    threshold: 80,
  },
};

const treatmentStep: PlanStep = {
  id: "treatment",
  title: "Your IVF Cycle",
  subtitle: "Active treatment with daily guidance",
  icon: "Syringe",
  education: {
    context:
      "Your IVF cycle is active. Track daily medications, appointments, and symptoms in the Care section.",
    details:
      "Your cycle progresses through several phases:\n\n• Baseline (2-3 days): Confirms ovaries are at rest\n• Stimulation (10-14 days): Daily injections help follicles grow\n• Trigger: Final maturation shot, precisely timed\n• Retrieval (~20 min): Eggs collected under light sedation\n• Fertilization with donor sperm (5-7 days): Embryos develop in the lab\n• Transfer (~5 min): Embryo placed in your uterus\n• Two-week wait: Before your pregnancy blood test\n\nYour Care page has your daily medication schedule, symptom tracker, and appointment calendar.",
  },
  actions: [
    {
      label: "Go to Care",
      variant: "primary",
      icon: "Heart",
      screen: "CareOverview",
    },
    { label: "Set your start date", variant: "outline", icon: "Calendar" },
  ],
  completionSignal: { type: "external" },
};

const waitStep: PlanStep = {
  id: "wait",
  title: "The Two-Week Wait",
  subtitle: "Between transfer and pregnancy test",
  icon: "Clock",
  education: {
    context:
      "The two-week wait (TWW) is the period between embryo transfer and your pregnancy blood test.",
    details:
      "Here's what's happening:\nDays 1-3: The embryo hatches and begins attaching to your uterine lining\nDays 4-5: Implantation begins\nDays 6-8: hCG hormone starts being produced if implantation is successful\nDays 9-14: hCG levels rise enough to be detectable by blood test\n\nWhat you can and can't control:\n• Continue progesterone as prescribed\n• Normal activity is fine. Bed rest does NOT improve outcomes\n• Avoid: hot baths, heavy alcohol, new intense exercise\n• Home pregnancy tests before day 10 are unreliable. Trust the blood test\n\nIt's normal to feel anxious, hopeful, terrified, obsessive about symptoms. Every feeling is valid.",
  },
  actions: [
    {
      label: "Log how you're feeling today",
      variant: "primary",
      icon: "Heart",
    },
    { label: "Support resources", variant: "secondary", icon: "Users" },
    { label: "When is my blood test?", variant: "outline", icon: "Calendar" },
  ],
  completionSignal: { type: "external" },
};

export const IVF_DONOR: JourneyDefinition = {
  key: "ivf-with-donor",
  gender: "female",
  goal: "pregnancy",
  pathway: "with-donor",
  title: "IVF",
  description: "In vitro fertilization with donor sperm",
  steps: [learnStep, testStep, clinicStep, prepStep, treatmentStep, waitStep],
};
